import {useQuery,useMutation,useQueryClient} from '@tanstack/react-query';
import {useLocation} from 'wouter';
import {X} from 'lucide-react';
import {workspaceUrl} from '@/pages/canvas/workspace-routes';
import {useCanvasStore} from '@/pages/canvas/canvas-store';
import {useToast} from '@/hooks/use-toast';
import type {firstAdsReadiness} from '@shared/firstAdsReadiness';
type Setup = ReturnType<typeof firstAdsReadiness> & {visible:boolean};
export function FirstAdsCard({brandId,onAddProduct,onReviewProduct}:{brandId:number;onAddProduct:()=>void;onReviewProduct:(productId:number)=>void}) {
 const [,navigate]=useLocation();
 const queryClient=useQueryClient();
 const {toast}=useToast();
 const folders=useQuery<Array<{id:number;brandId?:number;brand_id?:number;isFolder?:boolean;is_folder?:boolean}>>({
  queryKey:['/api/canvas/projects','rootOnly'],queryFn:async()=>{
   const response=await fetch('/api/canvas/projects?rootOnly=1');
   if(!response.ok)throw new Error('Could not load your brand workspace.');
   return response.json();
  },staleTime:30000,retry:3,
 });
 const key=['/api/brands',brandId,'first-ads'];
 const query=useQuery<Setup | null>({queryKey:key,queryFn:async()=>{
  const response=await fetch(`/api/brands/${brandId}/first-ads`);
  if(response.status===404){
   queryClient.setQueryData(['/api/brands',brandId],null);
   void queryClient.invalidateQueries({queryKey:['/api/brands'],exact:true});
   return null;
  }
  if(!response.ok)throw new Error('Could not check setup progress.');
  return response.json();
 },refetchInterval:q=>q.state.data?.visible?3000:false});
 const dismiss=useMutation({mutationFn:async()=>{
  const response=await fetch(`/api/brands/${brandId}/first-ads/dismiss`,{method:'POST'});
  if(!response.ok)throw new Error('Could not save dismissal. Please try again.');
 },onSuccess:()=>{queryClient.setQueryData<Setup>(key,old=>old?{...old,visible:false}:old);},onError:error=>toast({description:error.message,variant:'destructive'})});
 const retryBrand=useMutation({mutationFn:async()=>{
  const response=await fetch(`/api/brands/${brandId}/onboarding/start`,{method:'POST'});
  if(!response.ok)throw new Error('Could not restart brand analysis.');
 },onSuccess:()=>{void query.refetch();void queryClient.invalidateQueries({queryKey:['/api/brands',brandId,'onboarding']});},onError:error=>toast({description:error.message,variant:'destructive'})});
 const setup=query.data;
 if(!setup?.visible)return null;
 const busy=setup.brandBusy||setup.productBusy;
 function openTask(){
  if(!setup?.ready||query.isError)return;
  const folder=folders.data?.find(item=>(item.isFolder??item.is_folder)&&Number(item.brandId??item.brand_id)===brandId);
  if(!folder){void folders.refetch();toast({description:'Your brand workspace is still loading. Please try again.',variant:'destructive'});return;}
  const store=useCanvasStore.getState();
  store.setFolderProjectId(folder.id);
  store.addOpenFolder(folder.id);
  useCanvasStore.setState({activeProjectId:null,activeTaskId:null,activeBrandId:brandId,activeAdAccountId:null,pendingFirstAdBrandId:brandId});
  store.setActiveView('chat-only');
  navigate(workspaceUrl('chat-only',null,folder.id));
 }
 return <section aria-label="Create your ads" className="relative flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl border border-[#EAF0F7] bg-white px-4 py-3 pr-10">
  <div className="min-w-0"><h2 className="text-sm font-semibold">Create your first ads</h2><p role="status" className="mt-0.5 text-xs text-muted-foreground">{query.isError?'Unable to check progress. Retrying…':setup.message}</p></div>
  <div className="flex items-center gap-2">
   {setup.brandNeedsRetry&&<button disabled={retryBrand.isPending} className="text-xs underline" onClick={()=>retryBrand.mutate()}>Retry brand analysis</button>}
   {!setup.productId&&<button className="text-xs underline" onClick={onAddProduct}>Add a product</button>}
   <button data-testid="first-ads-review-products" disabled={!setup.productId} onClick={()=>{if(setup.productId)onReviewProduct(setup.productId);}} className="rounded-lg border border-[#C8D3E2] bg-transparent px-3 py-2 text-xs font-semibold text-[#1A1A1A] hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50">Review Products</button>
   <button data-testid="first-ads-create" disabled={!setup.ready||query.isError||folders.isPending} aria-busy={busy} onClick={()=>void openTask()} className="relative overflow-hidden rounded-lg bg-[#1A1A1A] px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#C8D3E2] disabled:text-[#334155]">
    {busy&&<span aria-hidden="true" data-testid="first-ads-shimmer" className="pointer-events-none absolute inset-0 motion-safe:animate-[shimmer_1.4s_infinite]" style={{background:'linear-gradient(90deg,transparent,rgba(255,255,255,.65),transparent)',backgroundSize:'200% 100%'}}/>}
    <span className="relative">Create Ads</span>
   </button>
  </div>
  <button aria-label="Dismiss first ads guide permanently" disabled={dismiss.isPending} onClick={()=>dismiss.mutate()} className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:bg-neutral-100"><X className="h-3.5 w-3.5"/></button>
 </section>;
}
